import { createColumnHelper, Row } from '@tanstack/react-table';
import CommonTable from 'components/table/CommonTable';
import { SortDirection } from 'components/table/Table.types';
import useServerFilteredTable from 'components/table/hooks/useServerFilteredTable';
import {
  IntegrationCategory,
  IntegrationEnum,
  IntegrationStatus,
  IntegrationType,
  InternalIntegrationSetting,
} from 'models/InternalIntegrationSetting';
import { integrationsSettingsQuery } from 'pages/channels/integrationSettingsQuery';
import { IntegrationsBadgeFilter } from 'pages/integrations/IntegrationsTabs.constants';
import useRedirectToIntegrationDetails from 'pages/integrations/hooks/useRedirectToIntegrationDetails';
import { useTranslation } from 'react-i18next';
import { API_MAX_LIMIT } from 'services/API.constants';
import { IntegrationsTableWrapper } from './IntegrationsTable.styles';
import IntegrationCell from './cells/IntegrationCell/IntegrationCell';
import StatusCell from './cells/statusCell/StatusCell';

const IntegrationsTable = ({
  category,
  isFeaturedTab = false,
}: {
  category?: IntegrationCategory;
  isFeaturedTab?: boolean;
}) => {
  const { t } = useTranslation();
  const { redirectToIntegrationDetails } = useRedirectToIntegrationDetails();

  const columnHelper = createColumnHelper<InternalIntegrationSetting>();
  const columns = [
    columnHelper.accessor('integration.name', {
      id: 'name',
      header: t('pageIntegrations.table.columns.integration'),
      enableSorting: true,
      cell: IntegrationCell,
      meta: {
        showCellTooltip: false,
      },
    }),
    columnHelper.accessor('integrationStats.connectedProperties', {
      header: t('pageIntegrations.table.columns.connectedProperties'),
      enableSorting: false,
      cell: ({ getValue }) => (getValue() === 0 ? '-' : getValue()),
    }),
    columnHelper.accessor('agencySettings.status', {
      id: 'actions',
      header: t('pageIntegrations.table.columns.status'),
      cell: StatusCell,
      enableSorting: true,
      meta: {
        showCellTooltip: false,
      },
    }),
  ];

  const getDefaultSorting = () => {
    // All and Featured tabs are sorted by badge priority by default
    if (isFeaturedTab || !category) {
      return {
        sort: 'SORT_BY_BADGE_PRIORITY',
        direction: SortDirection.DESC,
      };
    }

    return undefined;
  };

  const { isLoading, tableInstance } = useServerFilteredTable({
    columns,
    getQuery: (options) =>
      integrationsSettingsQuery({
        sorting: options.sorting,
        type: IntegrationType.INTEGRATION,
        category,
        defaultSorting: getDefaultSorting(),
        badgeFilter: isFeaturedTab
          ? IntegrationsBadgeFilter.WITH_BADGES
          : undefined,
      }),
    // The query returns all integrations without pagination
    pageSize: API_MAX_LIMIT,
  });

  const handleRowClick = (
    _: React.MouseEvent<HTMLTableRowElement>,
    row: Row<InternalIntegrationSetting>,
  ) => {
    const { status } = row.original.agencySettings;
    const { integration } = row.original;
    const isActiveOrPending =
      status === IntegrationStatus.ACTIVE ||
      status === IntegrationStatus.PENDING;

    if (!isActiveOrPending) {
      return;
    }

    redirectToIntegrationDetails(integration);
  };

  const getRowClassName = (row: Row<InternalIntegrationSetting>) => {
    const { status } = row.original.agencySettings;
    const { allowedForAgencyOnly } = row.original.integration;

    const isZapierIntegration =
      row.original.integration.enumId === IntegrationEnum.ZAPIER;
    const isActiveOrPending =
      status === IntegrationStatus.ACTIVE ||
      status === IntegrationStatus.PENDING;
    const hasPropertyLevelPage = !allowedForAgencyOnly;

    if (!isActiveOrPending) {
      return undefined;
    }

    if (isZapierIntegration || hasPropertyLevelPage) {
      return 'clickable-integration-row';
    }

    return undefined;
  };

  return (
    <IntegrationsTableWrapper>
      <CommonTable<InternalIntegrationSetting>
        tableInstance={tableInstance}
        isLoading={isLoading}
        data-testid={`integrations-table-${category?.toLowerCase() ?? 'all'}`}
        emptyState={t('pageIntegrations.table.emptyState')}
        handleRowClick={handleRowClick}
        getRowClassName={getRowClassName}
      />
    </IntegrationsTableWrapper>
  );
};

IntegrationsTable.defaultProps = {
  category: undefined,
  isFeaturedTab: false,
};

export default IntegrationsTable;
